import './years.scss';
import {controlsModel} from '../controls';
import map from '../map';
import d3 from 'd3';
import types, {getTypesObject} from '../types';
import debounce from 'lodash.debounce';

// element to wrap the d3 chart
let visId = 'years-vis',
    visElement = document.getElementById(visId);

// global selector and scales for d3 chart
let layer, x, y, timeScale;
let brush, domainStart, domainEnd;

// only initialize the chart once,
// just update after that.
let chartInitialized = false;

/**
 * Initialize the bar chart: create SVG element, scales/axes
 */
function drawYearsChart() {

    // get the current yearsData, formatted for D3
    let layers = getLayerData();

    // create d3 stacked bar chart to show the areas in the viewport
    let margin = {top: 40, right: 16, bottom: 29, left: 16},
        width = visElement.clientWidth - margin.left - margin.right,
        height = visElement.clientHeight - margin.top - margin.bottom;

    // scales (global, used again on update and brushing)
    x = d3.scale.ordinal()
        .rangeRoundBands([0, width], 0.3);

    y = d3.scale.linear()
        .rangeRound([height, 0]);

    domainStart = d3.time.year.offset(layers[0][0].x, 0);
    domainEnd = d3.time.year.offset(layers[0][layers[0].length-1].x, 0);
    domainEnd.setMonth(11,31); // December 31
    domainEnd.setHours(23,59,59); // 11:59pm

    timeScale = d3.time.scale()
        .domain([
            domainStart, // min date
            domainEnd // max date
        ])
        .range([0, width]);

    // axes
    let xAxis = d3.svg.axis()
        .scale(timeScale)
        .ticks(30) // @TODO: show all ticks dynamically
        .orient('bottom')
        .tickFormat(d3.time.format('%Y'));

    // render stacked bar chart
    let svg = d3.select('#' + visId).append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    // set domains (x never changes, y changes on update)
    x.domain(layers[0].map(function(d) { return d.x; }));
    y.domain([0, d3.max(layers[layers.length - 1], function(d) {
        return d.y0 + d.y;
    })]).nice();

    // brush for filtering by year
    brush = d3.svg.brush()
        .x(timeScale)
        .extent([domainStart, domainEnd])
        .on('brush', brushed)
        .on('brushend', brushEnd);

    // each "layer" is one layer-type from the map
    layer = svg.selectAll('.layer')
        .data(layers);

    // get step width and padding sizes for spacing
    let stepWidth = x.range()[1] - x.range()[0];
    let stepPadding = stepWidth - x.rangeBand();

    // one g.layer per layer-type
    layer.enter().append('g')
        .attr('class', 'layer')
        .attr('transform', 'translate('+(stepPadding / 2)+',0)') // compensate for the step padding between bands
        .style('fill', 'blue');

    // draw the bars, one per year in each g.layer
    updateBars();

    // add a slider track for the brush
    let brushTrack0 = timeScale(domainStart);
    let brushTrack1 = timeScale(domainEnd);

    svg.append('rect')
        .attr('x', brushTrack0)
        .attr('y', 0)
        .attr('transform', 'translate(0,-20)')
        .attr('class', 'brush-track')
        .attr('width', brushTrack1 - brushTrack0)
        .attr('height', 1);

    // brush element
    let gBrush = svg.append('g')
        .attr('class', 'brush')
        .call(brush);

    gBrush.selectAll('rect')
        .attr('height', 3)
        .attr('transform', 'translate(0,-21)');

    // brush resizing "handles" to sit on the track
    gBrush.selectAll('.resize').append('circle')
        .attr('class', 'brush-handle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('transform', 'translate(0,-20)')
        .attr('r', 10);

    // draw x-axis
    svg.append('g')
        .attr('class', 'axis x')
        .attr('transform', 'translate('+ (stepWidth / 2) +',' + height + ')') // center axis labels beneath bands
        .call(xAxis);

    // next time, run updateBars instead
    chartInitialized = true;
    // show elements now that the chart is loaded
    document.getElementById('years').classList.remove('loading');
}

/**
 * event handler for brush move/resize
 * rounds extent and enforces minimum/maximum
 */
function brushed() {
    let [e0, e1] = brush.extent(); // current extent
    let [n0, n1] = brush.extent(); // new extent

    n0 = d3.time.year.round(e0); // round lower
    n1 = d3.time.year.round(e1); // round upper

    n0 = d3.min([n0, d3.time.year.offset(n1, -1)]); // force at least 1 year extent
    n0 = d3.max([new Date(1985, 0, 1), n0]); // force minimum // @TODO: get years dynamically

    if (n1 <= d3.time.year.offset(n0, 1)) {
        n1 = d3.max([n1, d3.time.year.offset(n0, 1)]); // force at least 1 year extent
    }

    n1 = d3.time.second.offset(n1, -1); // 11:59pm

    // set brush extent
    d3.select(this).call(brush.extent([n0, n1]));
}
/**
 * event handler for brushend, updates vue model & filters
 */
function brushEnd() {
    controlsModel.years = brush.extent().map(function(d){
        return d.getFullYear();
    });
    controlsModel.updateFilters();
}

/**
 * format yearsData with d3 stack layout
 * changes years*types into types*years structure with x an y coords
 * @return {array} formatted data array
 */
function getLayerData() {
    let typesArray = types.map(function(t){ return t.name; });
    let parseDate = d3.time.format('%Y').parse;
    let data = controlsModel.yearsData;

    return d3.layout.stack()(typesArray.map(function(c) {
        return data.map(function(d) {
            // 0 if the layer is disabled
            return {x: parseDate(d.year), y: (d[c] || 0)};
        });
    }));
}

/**
 * update D3 bar chart with current data
 */
function updateBars() {
    let typesArray = types.map(function(t){ return t.name; });

    let rects = layer.selectAll('rect')
        .data(function(d) { return d; });

    rects.enter().append('rect')
        .attr('x', function(d) { return timeScale(d.x); })
        .attr('width', x.rangeBand() - 1)
        .attr('class', function(d,y,i){
            // css class based on type
            return 'type-' + typesArray[i];
        });

    rects.transition()
        .attr('y', function(d) { return y(d.y + d.y0); })
        .attr('height', function(d) { return y(d.y0) - y(d.y + d.y0); });
}

/**
 * update data, y-domain and bars on d3 chart
 */
function updateYearsChart() {
    let layers = getLayerData();

    y.domain([0, d3.max(layers[layers.length - 1], function(d) {
        return d.y0 + d.y;
    })]).nice();

    layer.data(layers);

    updateBars();
}

/**
 * build an object with the correct structure for the d3 stacked bar chart
 * @return {object} structured object
 */
export function getEmptyYearDataObject() {
    let emptyObj = {};

    for (let i = 1985; i <= 2011; i++) { // @TODO: get year range programatically
        let typesObject = getTypesObject();
        emptyObj[i] = typesObject;
    }

    return emptyObj;
}

/**
 * reformat object as array for d3 and update vue model
 * @param  {object} yearsDataObject formatted years data object
 */
export function formatYearsDataArray(yearsDataObject) {
    // reformat object as array for d3
    let yearsDataArray = [];
    let orderedYears = Object.keys(yearsDataObject).sort(); // ensure each year is in the same order
    for (let i = 0, numYears = orderedYears.length; i < numYears; i++) {
        let dataYear = yearsDataObject[orderedYears[i]];
        // add the year as a key
        dataYear.year = orderedYears[i];

        yearsDataArray.push(dataYear);
    }

    // update the vue model
    controlsModel.yearsData = yearsDataArray;
}

/**
 * Remove and re-render the entire d3 element (for resizing)
 */
function resizeHandler() {
    // preserve brush extent
    let brushExtent = brush.extent();

    // remove SVG element
    d3.select('#' + visId).select('svg').remove();
    // re-add SVG and render chart
    drawYearsChart();

    // set brush where it was before
    d3.select('#' + visId).select('g.brush')
        .call(brush.extent(brushExtent))
        .call(brush.event);
}

// debounce window resize event
let resizeHandlerDebounced = debounce(resizeHandler, 100);
window.addEventListener('resize', resizeHandlerDebounced);

// draw or update chart when yearsData finishes calculating
map.on('yearsData.updated', function(){
    // only initialize the chart once, after that just update
    if (!chartInitialized) {
        // render svg
        drawYearsChart();
    } else {
        updateYearsChart();
    }
});

// "Select All" years button
d3.select('#select-all-years').on('click', function(){
    // reset brush
    d3.select('#' + visId).select('g.brush').transition()
        .call(brush.extent([domainStart, domainEnd]))
        .call(brush.event);
});



// WEBPACK FOOTER //
// ./src/js/years/years.js